/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CardWithProgress } from '@app/app/page-modules/resource/models';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  ApprovalRequest,
  AssociateCategoryRequest,
  Audience,
  BrowseSettings,
  BrowseSettingsRequest,
  ContentUsage,
  DiagnosticsWithProgress,
  DisplayConfigRequest,
  RegistrationSettings,
} from '../../models';
import { ObservableResult } from '../../store';
import { RestClientService } from '../rest-client.service';
import { SharedCardDataService } from './card-data.service';

@Injectable()
export class ApiSharedCardDataService implements SharedCardDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  registerCardOpen(resourceUid: string): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/progress/openings`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorRegisterCardOpen'))),
    );
  }

  getCardDetails(cardUri: string): ObservableResult<CardWithProgress> {
    return this.client.get<DiagnosticsWithProgress>(Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardUri}`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorGetCard'))),
    );
  }

  associateTag(resourceUid: string, tagUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/settings/tags/${tagUid}`);
    return this.client.post<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorAddTag'))),
    );
  }

  dissociateTag(resourceUid: string, tagUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/settings/tags/${tagUid}`);
    return this.client.delete(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorRemoveTag'))),
    );
  }

  updateCardUri(resourceUid: string, newUri: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/settings/publishing/uri?newUri=${newUri}`);
    return this.client.post<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err) => this.handleErrorResponse(err)),
    );
  }

  updateAuthorDisplayConfig(resourceUid: string, request: DisplayConfigRequest): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/settings/author/config`);
    return this.client.post<void>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorUpdateAuthorVisibility'))),
    );
  }

  updateBrowseSettings(resourceUid: string, request: BrowseSettingsRequest): ObservableResult<BrowseSettings> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/browse-settings`);
    return this.client.put<BrowseSettings>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() =>
        ObservableResult.ofError(this.translocoService?.translate('translations.programs.publishSettings.browse.updateFailed')),
      ),
    );
  }

  publishCard(resourceUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/settings/publishing/publish`);
    return this.client.post<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorPublishCard'))),
    );
  }

  unpublishCard(resourceUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/settings/publishing/unpublish`);
    return this.client.post<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorUnPublishCard'))),
    );
  }

  archiveCard(resourceUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/settings/publishing/archive`);
    return this.client.post<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorArchiveCard'))),
    );
  }

  updateApprovalMethod(resourceUid: string, request: ApprovalRequest): ObservableResult<void> {
    return this.client
      .post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/settings/approvals`), request)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorUpdatingCardApproval'))),
      );
  }

  getReviewsCount(cardId: string): ObservableResult<number> {
    return this.client.get<number>(Location.joinWithSlash(environment.apiRootUrl || '', `reviews/count/${cardId}`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorGetCardReviewsCount'))),
    );
  }

  associateReviewer(resourceUid: string, userUid: string): ObservableResult<void> {
    return this.client
      .post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/settings/approvals/reviewers/${userUid}`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorUpdatingCardReviewers'))),
      );
  }

  dissociateReviewer(resourceUid: string, userUid: string): ObservableResult<void> {
    return this.client
      .delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `cards/${resourceUid}/settings/approvals/reviewers/${userUid}`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorUpdatingCardReviewers'))),
      );
  }

  associateCategory(cardUid: string, request: AssociateCategoryRequest): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardUid}/categories`), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorAddCardCategory'))),
    );
  }

  dissociateCategory(cardUid: string, categoryTagUid: string): ObservableResult<void> {
    return this.client.delete(Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardUid}/categories/${categoryTagUid}`)).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorRemoveCardCategory'))),
    );
  }

  associateTargetGroup(cardId: string, groupUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/targeting/groups/${groupUid}`);
    return this.client.post<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorAddTargetGroup'))),
    );
  }

  dissociateTargetGroup(cardId: string, groupUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/targeting/groups/${groupUid}`);
    return this.client.delete(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorRemoveTargetGroup'))),
    );
  }

  associateTargetUser(cardId: string, userUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/targeting/users/${userUid}`);
    return this.client.post<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorAddTargetUser'))),
    );
  }

  dissociateTargetUser(cardId: string, userUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/targeting/users/${userUid}`);
    return this.client.delete(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorRemoveTargetUser'))),
    );
  }

  updateAudience(cardId: string, audience: Audience): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/targeting/audience`);
    return this.client.put<void>(url, audience).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err: HttpErrorResponse) =>
        ObservableResult.ofError(
          err?.status === 400 && err.error?.message
            ? err.error.message
            : this.translocoService?.translate('translations.programs.publishSettings.audience.errors.audienceNotUpdated'),
        ),
      ),
    );
  }

  setRegistration(cardId: string, settings: RegistrationSettings): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/join-method/registration`);
    return this.client.put<void>(url, settings).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err: HttpErrorResponse) => this.joinMethodError(err)),
    );
  }

  clearRegistration(cardId: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/join-method/registration`);
    return this.client.delete(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err: HttpErrorResponse) => this.joinMethodError(err)),
    );
  }

  getTargetingUsages(cardId: string): ObservableResult<ContentUsage[]> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/targeting/usages`);
    return this.client.get<ContentUsage[]>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body ?? [])),
      catchError(() =>
        ObservableResult.ofError(this.translocoService?.translate('translations.programs.publishSettings.audience.usages.error')),
      ),
    );
  }

  updateTargetGroupSubscription(cardId: string, groupUid: string, force: boolean): ObservableResult<void> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `cards/${cardId}/targeting/groups/${groupUid}/subscriptions?forceSubscribe=${force}`,
    );
    return this.client.post<void>(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err: HttpErrorResponse) => this.audienceTargetError(err)),
    );
  }

  dissociateAllTargetGroups(cardId: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/targeting/groups`);
    return this.client.delete(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err: HttpErrorResponse) => this.audienceTargetError(err)),
    );
  }

  associateTargetProgram(cardId: string, programUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/targeting/programs/${programUid}`);
    return this.client.post<void>(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err: HttpErrorResponse) => this.audienceTargetError(err)),
    );
  }

  updateTargetProgramSubscription(cardId: string, programUid: string, force: boolean): ObservableResult<void> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `cards/${cardId}/targeting/programs/${programUid}/subscriptions?forceSubscribe=${force}`,
    );
    return this.client.post<void>(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err: HttpErrorResponse) => this.audienceTargetError(err)),
    );
  }

  dissociateTargetProgram(cardId: string, programUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `cards/${cardId}/targeting/programs/${programUid}`);
    return this.client.delete(url).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err: HttpErrorResponse) => this.audienceTargetError(err)),
    );
  }

  private joinMethodError(err: HttpErrorResponse): ObservableResult<void> {
    return ObservableResult.ofError(
      err?.status === 400 && err.error?.message
        ? err.error.message
        : this.translocoService?.translate('translations.programs.publishSettings.joinMethod.errors.notUpdated'),
    );
  }

  private audienceTargetError(err: HttpErrorResponse): ObservableResult<void> {
    return ObservableResult.ofError(
      err?.status === 400 && err.error?.message
        ? err.error.message
        : this.translocoService?.translate('translations.programs.publishSettings.audience.errors.audienceNotUpdated'),
    );
  }

  private handleErrorResponse(err: HttpErrorResponse) {
    if (err?.status === 409) {
      return ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorCardUriExists'));
    }
    return ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorUpdatingCardUri'));
  }
}
